import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, Check, BookOpenText, Download, BookOpen, Heart, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../hooks/use-toast';
import { SAMPLES } from '../mock/mock';

const BookCard = ({ book }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [format, setFormat] = useState('physical');
  const { addItem } = useCart();
  const { isFav, toggle } = useFavorites();
  const { toast } = useToast();
  const navigate = useNavigate();

  const sample = SAMPLES[book.id] || [book.blurb];
  const isFree = !!book.free && !!book.pdf;
  const favorited = isFav(book.id);

  const handleAdd = () => {
    addItem(book, 'physical');
    setOpen(false);
    toast({
      title: 'Added to cart',
      description: `${book.title} \u2014 Physical copy ($${book.physical})`,
    });
  };

  const readOnline = () => {
    setOpen(false);
    navigate(`/read/${book.id}`);
  };

  const toggleFav = (e) => {
    e.stopPropagation();
    toggle(book.id);
    toast({
      title: favorited ? 'Removed from your shelf' : 'Saved to your shelf',
      description: book.title,
    });
  };

  const shareBook = async () => {
    const url = `${window.location.origin}${isFree ? `/read/${book.id}` : '/buy'}`;
    const shareData = {
      title: `${book.title} — Limpid Reader`,
      text: `${book.title}: ${book.blurb}`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copied', description: 'Share it anywhere you like.' });
      }
    } catch (err) {
      /* user cancelled share */
    }
  };

  return (
    <>
      <div className="group flex flex-col relative">
        <button
          onClick={toggleFav}
          aria-label={favorited ? 'Remove from shelf' : 'Save to shelf'}
          className="absolute top-3 right-3 z-20 h-9 w-9 flex items-center justify-center rounded-full bg-white/85 backdrop-blur-sm shadow-md hover:bg-white transition-colors"
        >
          <Heart className={`h-4 w-4 ${favorited ? 'fill-red-500 text-red-500' : 'text-brand-brown/70'}`} />
        </button>
        <div className="book-flip aspect-[2/3] w-full mb-4 rounded-md shadow-lg shadow-brand-brown/10">
          <div className="book-flip-inner">
            <div className="book-face bg-sand">
              <img src={book.front} alt={book.title} className="w-full h-full object-cover" loading="lazy" />
              {isFree ? (
                <span className="absolute top-3 left-3 bg-green-700 text-white text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full">
                  Free Digital
                </span>
              ) : (
                book.tag && (
                  <span className="absolute top-3 left-3 bg-brand-ink/85 text-cream text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full">
                    {book.tag}
                  </span>
                )
              )}
            </div>
            <div className="book-face book-face-back bg-sand">
              <img src={book.back} alt={`${book.title} back cover`} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <h3 className="font-serif text-xl font-bold text-brand-ink leading-snug">{book.title}</h3>
          <p className="font-sans text-sm text-brand-brass italic mb-2">{book.subtitle}</p>
          <p className="text-sm text-brand-brown/80 leading-relaxed mb-3 line-clamp-3">{book.blurb}</p>

          <div className="mt-auto flex items-center justify-between pt-2">
            <div className="text-sm">
              {isFree ? (
                <>
                  <span className="text-green-700 font-semibold">Digital Free</span>
                  <span className="text-brand-brown/60"> &middot; Print ${book.physical}</span>
                </>
              ) : (
                <>
                  <span className="text-brand-ink font-semibold">${book.physical}</span>
                  <span className="text-brand-brown/60"> &middot; Print</span>
                </>
              )}
            </div>
            <Button
              onClick={() => setOpen(true)}
              className="bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full px-5 h-9 text-sm font-medium transition-colors"
            >
              Grab a Copy
            </Button>
          </div>

          {isFree ? (
            <button
              onClick={readOnline}
              className="mt-3 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-green-700 hover:text-green-800 transition-colors"
            >
              <BookOpen className="h-4 w-4" /> Read free online
            </button>
          ) : (
            <button
              onClick={() => setPreview(true)}
              className="mt-3 inline-flex items-center justify-center gap-1.5 text-sm font-medium text-brand-brass hover:text-brand-brown transition-colors"
            >
              <BookOpenText className="h-4 w-4" /> Read a sample
            </button>
          )}
        </div>
      </div>

      {/* Buy / access dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream border-brand-goldlight max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-ink">{book.title}</DialogTitle>
            <DialogDescription className="font-sans text-sm text-brand-brass italic">
              {book.subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-5 py-2">
            <img src={book.front} alt={book.title} className="w-28 rounded shadow-md object-cover" />
            <div>
              <p className="text-sm text-brand-brown/90 leading-relaxed">{book.blurb}</p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={toggleFav}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-brown hover:text-brand-brass transition-colors"
                >
                  <Heart className={`h-4 w-4 ${favorited ? 'fill-red-500 text-red-500' : ''}`} />
                  {favorited ? 'Saved' : 'Save'}
                </button>
                <button
                  onClick={shareBook}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-brown hover:text-brand-brass transition-colors"
                >
                  <Share2 className="h-4 w-4" /> Share
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="font-sans text-sm font-semibold text-brand-ink">Choose your format</p>
            {[
              { key: 'physical', label: 'Physical Copy', note: 'Printed & delivered to your door', price: `$${book.physical}` },
              ...(isFree
                ? [{
                    key: 'digital',
                    label: 'Digital Copy',
                    note: 'Read online or download \u2014 free',
                    price: 'Free',
                  }]
                : []),
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setFormat(opt.key)}
                className={`w-full flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-all ${
                  format === opt.key
                    ? 'border-brand-gold bg-sand'
                    : 'border-brand-goldlight/60 bg-white hover:border-brand-gold/60'
                }`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-semibold text-brand-ink">{opt.label}</span>
                    {format === opt.key && <Check className="h-4 w-4 text-brand-gold" />}
                  </div>
                  <p className="text-xs text-brand-brown/70">{opt.note}</p>
                </div>
                <span className={`font-serif text-lg font-bold ${opt.price === 'Free' ? 'text-green-700' : 'text-brand-ink'}`}>
                  {opt.price}
                </span>
              </button>
            ))}
          </div>

          {format === 'physical' ? (
            <Button onClick={handleAdd} className="w-full bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-11 mt-2 gap-2">
              <ShoppingCart className="h-4 w-4" /> Add to Cart
            </Button>
          ) : isFree ? (
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Link
                to={`/read/${book.id}`}
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-11 text-sm font-medium transition-colors"
              >
                <BookOpen className="h-4 w-4" /> Read Online
              </Link>
              <a
                href={book.pdf}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full h-11 text-sm font-medium transition-colors"
              >
                <Download className="h-4 w-4" /> Download
              </a>
            </div>
          ) : (
            <Button
              onClick={() => {
                addItem(book, 'digital');
                setOpen(false);
                toast({ title: 'Added to cart', description: `${book.title} \u2014 Digital copy ($${book.digital})` });
              }}
              className="w-full bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-11 mt-2 gap-2"
            >
              <ShoppingCart className="h-4 w-4" /> Add to Cart
            </Button>
          )}
        </DialogContent>
      </Dialog>

      {/* Sample preview dialog (non-free books) */}
      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="bg-cream border-brand-goldlight max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-ink">{book.title}</DialogTitle>
            <DialogDescription className="font-sans text-sm text-brand-brass italic">
              A sample from {book.subtitle}
            </DialogDescription>
          </DialogHeader>

          <div className="grid sm:grid-cols-[140px_1fr] gap-6 py-2">
            <img src={book.front} alt={book.title} className="w-full rounded-md shadow-lg object-cover" />
            <div className="max-h-72 overflow-y-auto pr-2">
              <div className="relative bg-white rounded-lg p-6 shadow-inner border border-brand-goldlight/50">
                <span className="block font-serif text-3xl text-brand-goldlight leading-none mb-2">&ldquo;</span>
                {sample.map((para, i) => (
                  <p
                    key={i}
                    className="font-serif text-[15px] text-brand-ink/90 leading-relaxed mb-4 first-letter:text-3xl first-letter:font-bold first-letter:text-brand-brass first-letter:mr-1 first-letter:float-left"
                  >
                    {para}
                  </p>
                ))}
                <p className="text-xs text-brand-brown/60 italic mt-2">Preview excerpt &mdash; continues in the full book.</p>
              </div>
            </div>
          </div>

          <Button
            onClick={() => {
              setPreview(false);
              setOpen(true);
            }}
            className="w-full bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full h-11 gap-2"
          >
            <ShoppingCart className="h-4 w-4" /> {`Grab a Copy \u2014 $${book.physical}`}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BookCard;
